import { Type } from 'class-transformer';
import {
  ArrayMaxSize,
  ArrayMinSize,
  ArrayUnique,
  IsArray,
  IsEmail,
  IsIn,
  IsInt,
  IsNotEmpty,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  MaxLength,
  Min,
  ValidateNested,
} from 'class-validator';
import {
  SURVEY_DEADLINE_MODES,
  SURVEY_DOCUMENT_VALIDITY_MONTHS_MAX,
  SURVEY_DOCUMENT_VALIDITY_MONTHS_MIN,
  SURVEY_INVITE_EMAIL_MAX_LENGTH,
  SURVEY_INVITE_ID_MAX_LENGTH,
  SURVEY_INVITE_MAX_DAYS,
  SURVEY_INVITE_MAX_TEMPLATES,
  SURVEY_INVITE_MESSAGE_MAX_LENGTH,
  SURVEY_INVITE_RECIPIENT_NAME_MAX_LENGTH,
  type SurveyDeadlineMode,
} from '../../types/survey-invite';
import { OptionalStringTransform } from '../../validation/optional-string.transformer';

/**
 * Scadenza scelta per questo cliente su un questionario della richiesta, al posto di quella del
 * template. La coerenza modo/mesi la decide il server con `resolveSurveyDeadlineConfig`
 * (NONE senza mesi, SINGLE/RECURRING con mesi da 1 a 120).
 */
export class SurveyInviteDeadlineOverrideDto {
  @IsUUID('loose')
  templateId!: string;

  @IsIn(SURVEY_DEADLINE_MODES)
  deadlineMode!: SurveyDeadlineMode;

  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(SURVEY_DOCUMENT_VALIDITY_MONTHS_MIN)
  @Max(SURVEY_DOCUMENT_VALIDITY_MONTHS_MAX)
  documentValidityMonths?: number | null;
}

export class CreateSurveyInviteDto {
  @IsString()
  @IsNotEmpty()
  @MaxLength(SURVEY_INVITE_ID_MAX_LENGTH)
  tenantId!: string;

  @IsArray()
  @ArrayMinSize(1)
  @ArrayMaxSize(SURVEY_INVITE_MAX_TEMPLATES)
  @ArrayUnique()
  @IsUUID('loose', { each: true })
  templateIds!: string[];

  @Type(() => Number)
  @IsInt()
  @Min(1)
  @Max(SURVEY_INVITE_MAX_DAYS)
  expiresInDays!: number;

  @IsOptional()
  @OptionalStringTransform()
  @IsEmail()
  @MaxLength(SURVEY_INVITE_EMAIL_MAX_LENGTH)
  recipientEmail?: string;

  @IsOptional()
  @OptionalStringTransform()
  @IsString()
  @MaxLength(SURVEY_INVITE_RECIPIENT_NAME_MAX_LENGTH)
  recipientName?: string;

  @IsOptional()
  @OptionalStringTransform()
  @IsString()
  @MaxLength(SURVEY_INVITE_MESSAGE_MAX_LENGTH)
  message?: string;

  /**
   * Solo i questionari con una scadenza diversa dal template; assente o vuoto vale "come il
   * template". Ogni `templateId` deve stare in `templateIds` ed essere unico (controllo server).
   */
  @IsOptional()
  @IsArray()
  @ArrayMaxSize(SURVEY_INVITE_MAX_TEMPLATES)
  @ValidateNested({ each: true })
  @Type(() => SurveyInviteDeadlineOverrideDto)
  deadlineOverrides?: SurveyInviteDeadlineOverrideDto[];
}
